import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Footprints, Link2, Printer, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import TrailMap from "@/components/TrailMap";
import AIContentBlock from "@/components/AIContentBlock";

export default function TrailPage() {
  const { slug, citySlug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/countries/${slug}/cities/${citySlug}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [slug, citySlug]);

  if (notFound) return <div className="py-24 text-center text-slate-500" data-testid="trail-not-found">Trail not found. <Link to="/explore" className="text-pink-600 font-bold">Browse the world</Link></div>;
  if (!data) return <div className="min-h-[50vh] flex items-center justify-center"><span className="eq-bars" style={{ height: 36 }}><span /><span /><span /><span /><span /></span></div>;

  const { country, city, venues } = data;
  const stops = venues.filter((v) => v.lat && v.lng);
  const shareUrl = `${window.location.origin}/places/${slug}/cities/${citySlug}/trail`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Trail link copied — share it with fellow travelers!");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      ok ? toast.success("Trail link copied — share it with fellow travelers!") : toast.error("Could not copy. Long-press the address bar to copy the URL.");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={`trail-page-${citySlug}`}>
      <Link to={`/places/${slug}/cities/${citySlug}`} data-testid="trail-back-link" className="print-hide inline-flex items-center gap-1 text-xs font-bold text-pink-600 hover:text-pink-700 mb-4">
        <ChevronLeft className="w-3.5 h-3.5" /> Back to {city.name}
      </Link>

      <div className="flex flex-wrap items-start gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-purple-600 mb-2 flex items-center gap-2"><Footprints className="w-4 h-4" /> Music Trail</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900">{city.name} <span className="gradient-text">Music Trail</span></h1>
          <p className="mt-2 text-sm text-slate-500">{country.flag} {country.name} · {stops.length} stops · a walkable route through the city's music landmarks</p>
        </div>
        <div className="ml-auto flex gap-3 print-hide">
          <button data-testid="trail-copy-link-btn" onClick={copyLink} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-pink-300 text-pink-600 text-xs font-black hover:bg-pink-50 transition-colors">
            <Link2 className="w-3.5 h-3.5" /> Copy share link
          </button>
          <button data-testid="trail-print-btn" onClick={() => window.print()} className="gradient-btn inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-white text-xs font-black">
            <Printer className="w-3.5 h-3.5" /> Print trail
          </button>
        </div>
      </div>

      <div className="print-hide">
        <AIContentBlock pageKey={`trail-${slug}-${citySlug}`} topic={`Walking the music trail of ${city.name}, ${country.name} — what travelers will hear and feel along the route`} compact />
      </div>

      {stops.length > 0 ? (
        <>
          <div className="mt-6 print-hide"><TrailMap stops={stops} /></div>

          <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-4 max-w-md trail-stop-print" data-testid="trail-qr-card">
            <QRCodeSVG value={shareUrl} size={92} fgColor="#0f172a" data-testid="trail-qr-code" />
            <p className="text-sm text-slate-600 leading-relaxed"><span className="font-bold text-slate-900">Take it with you.</span> Scan to reopen this trail on your phone mid-walk — map, stops and hours included.</p>
          </div>

          <ol className="mt-8 space-y-4" data-testid="trail-print-stops">
            {stops.map((v, i) => (
              <li key={v.id} className="bg-white rounded-2xl border border-slate-100 p-5 flex gap-4 trail-stop-print" data-testid={`trail-stop-${v.id}`}>
                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-purple-600 text-white text-sm font-black flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <Link to={`/venues/${v.id}`} className="font-bold text-slate-900 hover:text-pink-600 transition-colors" data-testid={`trail-stop-link-${v.id}`}>{v.name}</Link>
                  <p className="text-[11px] font-black uppercase tracking-wide text-amber-600">{v.type}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{v.blurb}</p>
                  <p className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {v.hours && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-purple-500" /> {v.hours}</span>}
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-pink-500" /> {v.lat.toFixed(4)}, {v.lng.toFixed(4)}</span>
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <p className="mt-8 text-xs text-slate-400 text-center">
            {city.name} Music Trail · MusicPulse.pro · <span className="print-only-url">{shareUrl}</span>
          </p>
        </>
      ) : (
        <p className="mt-10 text-sm text-slate-500 bg-white rounded-2xl border border-slate-100 p-5" data-testid="trail-empty">No mapped venues yet for this city.</p>
      )}
    </div>
  );
}
